import React, { useEffect, useRef, useState } from 'react';
import { useApp } from '@/context/AppContext';

// Named video themes: each pairs a self-hosted video + poster + overlay treatment.
export const VIDEO_THEMES = {
  original: {
    label: 'Original',
    poster: '/assets/bg-poster.jpg',
    sources: [
      { src: '/assets/bg-video.mp4', type: 'video/mp4' },
      { src: '/assets/bg-video.webm', type: 'video/webm' },
    ],
    overlayClass: 'video-overlay',
  },
  cinematic: {
    label: 'Cinematic',
    poster: '/assets/bg-poster-2.jpg',
    sources: [
      { src: '/assets/bg-video-2.mp4', type: 'video/mp4' },
      { src: '/assets/bg-video-2.webm', type: 'video/webm' },
    ],
    overlayClass: 'video-overlay-cinematic',
  },
};

const FADE_MS = 400;

// Rendered ONCE at the app root so the video never reloads/restarts on navigation.
// Exactly one <video> element exists; on theme switch its sources/poster swap
// behind a ~400ms opacity fade (no hard cut, nothing else on the page reloads).
export default function VideoBackground() {
  const { videoTheme } = useApp();
  const videoRef = useRef(null);
  const [displayTheme, setDisplayTheme] = useState(videoTheme); // theme currently rendered
  const [faded, setFaded] = useState(false); // true → layer at opacity 0 during the swap
  const [useVideo, setUseVideo] = useState(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return false;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const small = window.matchMedia('(max-width: 767px)').matches;
    return !reduced && !small;
  });

  const cfg = VIDEO_THEMES[displayTheme] || VIDEO_THEMES.original;

  // Crossfade sequence: fade out → swap source/poster → fade back in when playable
  useEffect(() => {
    if (videoTheme === displayTheme) return undefined;
    setFaded(true);
    const t = setTimeout(() => setDisplayTheme(videoTheme), FADE_MS);
    return () => clearTimeout(t);
  }, [videoTheme, displayTheme]);

  const handleReady = () => setFaded(false);

  // Safety: never stay faded out for long even if canplay fails to fire
  useEffect(() => {
    if (!faded) return undefined;
    const t = setTimeout(() => setFaded(false), FADE_MS + 1500);
    return () => clearTimeout(t);
  }, [faded]);

  // Poster-only mode has no canplay event; un-fade as soon as the theme swaps
  useEffect(() => {
    if (!useVideo && displayTheme === videoTheme) setFaded(false);
  }, [useVideo, displayTheme, videoTheme]);

  // React to changes in reduced-motion preference or viewport size
  useEffect(() => {
    if (!window.matchMedia) return undefined;
    const reducedMq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const smallMq = window.matchMedia('(max-width: 767px)');
    const update = () => setUseVideo(!reducedMq.matches && !smallMq.matches);
    reducedMq.addEventListener('change', update);
    smallMq.addEventListener('change', update);
    return () => {
      reducedMq.removeEventListener('change', update);
      smallMq.removeEventListener('change', update);
    };
  }, []);

  // Pause when the tab is hidden, resume on return; also kick autoplay on mount/swap
  useEffect(() => {
    if (!useVideo) return undefined;
    const v = videoRef.current;
    if (v) {
      const tryPlay = () => v.play().catch(() => {});
      tryPlay();
      v.addEventListener('canplay', tryPlay, { once: true });
    }
    const onVis = () => {
      const vid = videoRef.current;
      if (!vid) return;
      if (document.hidden) vid.pause();
      else vid.play().catch(() => {});
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [useVideo, displayTheme]);

  return (
    <div aria-hidden="true" data-testid="video-background" data-video-theme-active={displayTheme} className="pointer-events-none fixed inset-0 z-0 select-none bg-[#070b13]">
      <div
        className="absolute inset-0 transition-opacity ease-in-out"
        style={{ opacity: faded ? 0 : 1, transitionDuration: `${FADE_MS}ms` }}
      >
        {useVideo ? (
          <video
            key={displayTheme}
            ref={videoRef}
            autoPlay
            loop
            muted
            playsInline
            preload="auto"
            poster={cfg.poster}
            onCanPlay={handleReady}
            className="h-full w-full object-cover"
          >
            {cfg.sources.map((s) => <source key={s.src} src={s.src} type={s.type} />)}
          </video>
        ) : (
          <img src={cfg.poster} alt="" onLoad={handleReady} className="h-full w-full object-cover" />
        )}
        <div className={`${cfg.overlayClass} absolute inset-0`} />
      </div>
    </div>
  );
}
